import { Button } from '@monorepo/shared/componentsV2/Button';
import { TextField } from '@monorepo/shared/componentsV2/fields/TextField';
import { BaseModal } from '@monorepo/shared/componentsV2/modals/BaseModal';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { DocumentFileResponse } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useUpdateSiteDocument } from '../hooks/useUpdateSiteDocument';
import { useSiteDocumentsList } from '../hooks/useSiteDocumentsList';
import { useSiteFolderDocumentsList } from '../hooks/useSiteFolderDocumentsList';

interface RenameSiteDocumentModalProps {
  organizationId: string;
  projectId: string;
  document: DocumentFileResponse;
  onClose: () => void;
}

interface FormValues {
  name: string;
}

export function RenameSiteDocumentModal({
  organizationId,
  projectId,
  document,
  onClose,
}: RenameSiteDocumentModalProps) {
  const { showUserFriendlyErrorToast, success } = useToast();

  const isInFolder = !!document.folderId;
  const { documents: folderDocuments } = useSiteFolderDocumentsList({
    organizationId,
    projectId,
    folderId: isInFolder ? (document.folderId as string) : undefined,
  });
  const { siteDocuments } = useSiteDocumentsList({
    organizationId,
    projectId,
    config: { enabled: !isInFolder },
  });
  const peerDocuments = useMemo<DocumentFileResponse[]>(() => {
    if (isInFolder) return folderDocuments ?? [];
    return (siteDocuments?.files ?? []).filter((file) => file.folderId == null);
  }, [folderDocuments, isInFolder, siteDocuments]);

  const [rename] = useUpdateSiteDocument({ config: { throwOnError: true } });

  const validate = useCallback(
    (values: FormValues) => {
      const errors: Partial<Record<keyof FormValues, string>> = {};
      const trimmed = values.name?.trim() ?? '';
      if (!trimmed) {
        errors.name = 'Document name is required.';
        return errors;
      }
      const isDuplicate = peerDocuments.some((existing) => {
        if (existing.id === document.id) return false;
        return existing.name.toLowerCase() === trimmed.toLowerCase();
      });
      if (isDuplicate) {
        errors.name = isInFolder
          ? 'A document with this name already exists in this folder. Names must be unique within a folder.'
          : 'A document with this name already exists for this site. Names must be unique.';
      }
      return errors;
    },
    [document.id, isInFolder, peerDocuments],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      const name = values.name.trim();
      try {
        await rename({
          organizationId,
          projectId,
          documentId: document.id,
          document: { name },
        });
        success(`Document was renamed to "${name}".`);
        onClose();
      } catch (err) {
        showUserFriendlyErrorToast(err, 'Unable to rename document.');
      }
    },
    [
      document.id,
      onClose,
      organizationId,
      projectId,
      rename,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  return (
    <Form<FormValues>
      onSubmit={onSubmit}
      validate={validate}
      initialValues={{ name: document.name }}
      subscription={{
        submitting: true,
        pristine: true,
        hasValidationErrors: true,
      }}
    >
      {({ handleSubmit, submitting, pristine, hasValidationErrors }) => (
        <BaseModal
          open
          onClose={submitting ? undefined : onClose}
          showCloseButton
          title="Rename document"
          maxWidth="sm"
          buttons={[
            <Button
              key="cancel"
              variant="text"
              color="primary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>,
            <Button
              key="save"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={submitting || pristine || hasValidationErrors}
            >
              {submitting ? 'Saving...' : 'Save'}
            </Button>,
          ]}
        >
          <TextField
            name="name"
            label="Document name"
            autoFocus
            required
            fullWidth
            forceShowErrors={false}
          />
        </BaseModal>
      )}
    </Form>
  );
}
